/*
 *  Copyright (C) 2025 - SimpliCity Digital Inc - All Rights Reserved
 */

import { FormControlLabel, FormGroup, Switch } from "@mui/material";
import Typography from "@mui/material/Typography";
import { useUpdateComponent } from "cms/components/hooks/use-update-component";
import AceEditor from "react-ace";
import { isStringNotEmpty } from "utils/empty-exists";

// ace imports
import "ace-builds/src-noconflict/ext-language_tools";
import "ace-builds/src-noconflict/ext-spellcheck";
import "ace-builds/src-noconflict/mode-html";
import "ace-builds/src-noconflict/theme-chrome";
import { useMemo } from "react";

const editorTheme = "chrome";

/**
 * @param {string=} html
 */
const defaultHtml = (html) => {
    if (isStringNotEmpty(html)) {
        return html;
    }

    return "<!-- SimpliCity CMS Embed placeholder -->";
};

/**
 * @param {string=} html
 */
const defaultScript = (html) => {
    if (isStringNotEmpty(html)) {
        return html;
    }

    return "/* <!-- SimpliCity CMS Embed placeholder --> */";
};

const EmbedComponentEditor = () => {
    const { component, update } = useUpdateComponent();

    const componentWithDefaults = useMemo(() => {
        let {
            html,
            script,
            isResponsive,
            enableScript,
        } = component;
        isResponsive = isResponsive || false;
        if (isStringNotEmpty(isResponsive)) {
            // should not be a string
            isResponsive = isResponsive === "on";
        }

        enableScript = enableScript || false;
        if (isStringNotEmpty(enableScript)) {
            // should not be a string
            enableScript = enableScript === "on";
        }

        html = defaultHtml(html);
        script = defaultScript(script);

        return {
            ...component,
            html,
            script,
            isResponsive,
            enableScript,
        };
    }, [component]);

    const handleEditorChange = (value) => {
        const html = defaultHtml(value);
        update("html", html);
    };

    const handleEditorScriptChange = (value) => {
        const script = defaultScript(value);
        update("script", script);
    };

    const onChangeIsResponsive = (event) => {
        const checked = event.target.checked;
        update("isResponsive", checked);
    };

    const onChangeEnableScript = (event) => {
        const checked = event.target.checked;
        update("enableScript", checked);
    };

    return (
        <div className='simplicity-editor-frame'>
            <div className='simplicity-composer-embed'>
                <Typography variant='overline' color={"text.secondary"}>
                    HTML Embed Code:
                </Typography>
                {componentWithDefaults.html &&
                    <AceEditor
                        value={componentWithDefaults.html}
                        mode='html'
                        theme={editorTheme}
                        onChange={handleEditorChange}
                        name={`simplicity-json-editor-${componentWithDefaults.guid}`}
                        editorProps={{ $blockScrolling: true }}
                        setOptions={{
                            useWorker: false,
                            enableBasicAutocompletion: false,
                            enableLiveAutocompletion: false,
                            enableSnippets: false,
                            showPrintMargin: false,
                        }}
                        width='100%'
                        tabsize='2'
                    />
                }
                <Typography variant='overline' color={"text.secondary"}>
                    Make IFrame Responsive (16:9):
                </Typography>
                <FormGroup>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={componentWithDefaults.isResponsive}
                                onChange={onChangeIsResponsive}
                            />
                        }
                        label='Enabled'
                    />
                </FormGroup>

                <Typography variant='overline' color={"text.secondary"}>
                    Script Code:
                </Typography>
                {componentWithDefaults.enableScript &&
                    <AceEditor
                        value={componentWithDefaults.script}
                        mode='html'
                        theme={editorTheme}
                        onChange={handleEditorScriptChange}
                        name={`simplicity-json-editor-${componentWithDefaults.guid}`}
                        editorProps={{ $blockScrolling: true }}
                        setOptions={{
                            useWorker: false,
                            enableBasicAutocompletion: false,
                            enableLiveAutocompletion: false,
                            enableSnippets: false,
                            showPrintMargin: false,
                        }}
                        width='100%'
                        tabsize='2'
                    />
                }
                <Typography variant='overline' color={"text.secondary"}>
                    Enable Script:
                </Typography>
                <FormGroup>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={componentWithDefaults.enableScript}
                                onChange={onChangeEnableScript}
                            />
                        }
                        label='Enabled'
                    />
                </FormGroup>
            </div>
        </div>
    );
};

EmbedComponentEditor.displayName = "EmbedComponentEditor";
export { EmbedComponentEditor };
